import React, { useContext } from 'react';
import classnames from 'classnames';
import Loading from '@ifit/mp-loading';
import ScrollingList from '@ifit/mp-scrolling-list';
import SectionHeader from '@ifit/mp-section-header';
import ContentItem, { IContentItemAnalytics } from '../content-item';
import SectionHeaderLink from '../section-header-link';
import ViewAllLink from '../view-all-link';
import { WeakType } from '../../typescript-migration';
import { isMobile } from '../../helpers/responsive';
import { useDeviceType } from '../../store/device-type';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import { Themes } from '@ifit/themes/withTheme';
import ReloadScrollingSection from '../reload-scrolling-section';
import './index.scss';

interface IListData {
  data: WeakType[];
  hasMoreItems?: boolean;
}

interface IProps {
  title: string;
  translatedTitle?: string;
  listData: IListData;
  route?: string;
  large?: boolean;
  isLoading?: boolean;
  showCategory?: boolean;
  showSubcategory?: boolean;
  themeClass?: string;
  analytics?: IContentItemAnalytics;
  reload?: () => void;
  isError?: boolean;
}

const SectionHeaderPlain = SectionHeader();

const ScrollingSection: React.ForwardRefRenderFunction<WeakType, IProps> = (
  {
    isLoading,
    listData,
    route,
    showCategory,
    showSubcategory,
    large,
    title,
    translatedTitle,
    analytics,
    reload,
    isError,
  },
  ref,
) => {
  const { theme } = useContext(ThemeContext);
  const themeClass = Themes[theme];
  const deviceType = useDeviceType();
  const mobile = isMobile(deviceType);
  const rowTitle = translatedTitle ? translatedTitle : title;

  let renderReloadSection = false;
  let content;

  if (listData && listData?.data?.length && !isLoading) {
    listData.data?.forEach((item, index) => {
      if (item.data) {
        item.data.forEach((innerItem, innerIndex) => {
          innerItem.index = innerIndex;
          innerItem.residesIn = item.title;
        });
      } else {
        item.index = index;
      }
    });

    content = (
      <ScrollingList
        data={listData?.data}
        cardWidth={null}
        cardSpacing={null}
        hasMoreItems={listData?.hasMoreItems}
      >
        {item => (
          <ContentItem
            showCategory={showCategory}
            showSubcategory={showSubcategory}
            item={item}
            large={large}
            analyticsData={{
              engagementData: {
                ...analytics?.engagementData,
                rowTitle: item.residesIn ? `${title}:${item.residesIn}` : title,
                columnNumber: item.index,
              },
            }}
          />
        )}
      </ScrollingList>
    );
  } else if (isLoading || (!isError && !listData?.data)) {
    content = (
      <div
        className={classnames('loading-wrapper', {
          large: !mobile && large,
          small: mobile,
          'trainer-card': route === '/trainers',
        })}
      >
        <Loading absolute data-testid="loading-spinner" />
      </div>
    );
  } else if (reload && isError) {
    renderReloadSection = true;
    content = <ReloadScrollingSection reload={reload} />;
  } else {
    // If listData.data.length === 0, there is no error and no reload option is provided, do not render section
    return null;
  }

  return (
    <section
      className={classnames('section', {
        'failed-reload': renderReloadSection,
      })}
      ref={ref}
    >
      {route && (
        <SectionHeaderLink
          deviceType={deviceType}
          to={route}
          title={rowTitle}
          hasMoreItems={listData?.hasMoreItems}
          viewAll={<ViewAllLink themeClass={themeClass} to={route} />}
        />
      )}
      {!route && (
        <SectionHeaderPlain deviceType={deviceType} title={rowTitle} />
      )}
      {content}
    </section>
  );
};

export default React.forwardRef(ScrollingSection);
